/**
 * xAI — Grok Imagine (image) and Grok Imagine Video.
 *
 * The user-provided endpoint slugs ("xai/grok-imagine-image-quality/text-to-image"
 * etc.) look like aggregator-style slugs rather than direct xAI API paths.
 * We implement against xAI's documented surface (https://api.x.ai/v1) using the
 * model family "grok-2-image" / "grok-imagine-video" naming and let the proxy
 * remap if the real endpoint differs. See docs/KNOWN_UNKNOWNS.md.
 */
import type { GenerateRequest, GenerateResult, ProviderAdapter, ModelDescriptor } from '../router/types';
import { proxy, ok, fail, queued } from './common';

const BASE = 'https://api.x.ai/v1';

export const XAI_MODELS: ModelDescriptor[] = [
  {
    id: 'grok-imagine',
    label: 'Grok Imagine',
    mode: 'image',
    caps: { multiRef: 3, edit: true, aspects: ['1:1','3:4','4:3','9:16','16:9'], maxRes: '1080p' }
  },
  {
    id: 'grok-imagine-video',
    label: 'Grok Imagine Video',
    mode: 'video',
    caps: {
      multiRef: 7, startEndFrame: true, audio: true, edit: true,
      bgRemove: false, maxDurationSec: 15,
      aspects: ['16:9','9:16','1:1'], maxRes: '720p'
    }
  }
];

async function imageGen(req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  const isEdit = (req.refs?.length ?? 0) > 0;
  const endpoint = `${BASE}/images/${isEdit ? 'edits' : 'generations'}`;
  const body: any = {
    model: 'grok-imagine',
    prompt: req.prompt,
    n: 1,
    response_format: 'b64_json',
    size: req.aspect ?? '1:1'
  };
  if (isEdit) body.image_urls = (req.refs ?? []).slice(0, 3).map(r => r.url);
  const res = await proxy<any>({ provider: 'xai', endpoint, body, apiKey, authStyle: 'bearer' });
  const data = res.data ?? [];
  if (!data.length) return fail('No image returned', res);
  return ok(data.map((d: any) => ({
    url: d.url ?? `data:image/png;base64,${d.b64_json}`,
    mime: 'image/png'
  })));
}

async function videoCreate(req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/videos/generations`;
  const body: any = {
    model: 'grok-imagine-video',
    prompt: req.prompt,
    duration: req.durationSec ?? 6,
    resolution: req.resolution ?? '720p',
    aspect_ratio: req.aspect ?? '16:9',
    reference_images: (req.refs ?? []).slice(0, 7).map(r => r.url),
    reference_video: req.refVideos?.[0]?.url,
    start_frame: req.startFrame?.url,
    end_frame: req.endFrame?.url,
    generate_audio: true
  };
  const res = await proxy<any>({ provider: 'xai', endpoint, body, apiKey, authStyle: 'bearer' });
  const id = res.id ?? res.task_id;
  if (!id) return fail('No task id returned', res);
  return queued(id, res);
}

async function videoPoll(taskId: string, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/videos/generations/${taskId}`;
  const res = await proxy<any>({ provider: 'xai', endpoint, method: 'GET', apiKey, authStyle: 'bearer' });
  const status = (res.status ?? '').toLowerCase();
  if (status === 'succeeded' || status === 'completed') {
    const out = res.output ?? res.outputs ?? [];
    return ok((Array.isArray(out) ? out : [out]).map((o: any) => ({
      url: o.url ?? o.video_url,
      mime: 'video/mp4'
    })));
  }
  if (status === 'failed') return fail(res.error?.message ?? 'xAI task failed', res);
  return { status: 'running', taskId, raw: res };
}

export const xai: ProviderAdapter = {
  id: 'xai',
  models: XAI_MODELS,
  async generate(modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
    if (modelId === 'grok-imagine') return imageGen(req, apiKey);
    if (modelId === 'grok-imagine-video') return videoCreate(req, apiKey);
    return fail(`Unknown xAI model: ${modelId}`);
  },
  poll: videoPoll
};